import { useEffect, useRef, useState } from 'react';
import { api, type AiTurn, type Chat } from '../lib/api';
import { ask, type Catalogue } from '../lib/askarchive';
import { fold } from '../lib/search';

export interface Said { mine: boolean; text: string }

/**
 * The thread, held by App rather than here.
 *
 * It used to live in this component's own state, which meant clicking Chats
 * unmounted it and coming back showed an empty panel — the conversation was
 * lost by navigating away from it. Nothing is written anywhere: the thread
 * lives as long as the tab does, and closing the tab still ends it.
 */
export interface Thread {
  said: Said[];
  turns: AiTurn[];
  /**
   * The conversation the thread is pinned to, if one was picked.
   *
   * Sticky on purpose: picking used to last one message, so asking three
   * things about the same group meant typing "/" three times. It holds until
   * another is picked or it is cleared.
   */
  focus?: string;
}

/**
 * A conversation about every conversation.
 *
 * It sits in the rail beside Chats and Media rather than inside any one
 * conversation, because what it is for is the questions that span them —
 * "when did we agree that", "what did we decide about the kitchen". A button
 * in a chat header could only ever answer about that chat.
 *
 * The thread lives in this component and nowhere else. Closing the tab ends
 * it: we are not keeping a second archive of what people asked.
 */
export function AskPanel({ chats, titleOf, accountKey, onEnable, onBack, thread, onThread,
                          pinned }: {
  chats: Chat[];
  titleOf: (c: Chat) => string;
  accountKey: Uint8Array;
  onEnable: () => void;
  /** On a phone this pane is the whole screen, so it needs the way out. */
  onBack: () => void;
  thread: Thread;
  onThread: (next: Thread) => void;
  /**
   * Opened from inside a conversation, and about that one only.
   *
   * The panel then has no conversation to choose, so "/" goes away and the
   * chip loses its ×: the way out is closing the panel, which is where the
   * person came from. Each conversation keeps its own thread, so going back
   * to one finds the questions already asked about it.
   */
  pinned?: string;
}) {
  // Asked here rather than carried down from the session: the session's
  // account view predates the switch, and a copy that goes stale would offer
  // to answer questions the server is about to refuse.
  const [enabled, setEnabled] = useState<boolean | null>(null);
  useEffect(() => { api.profile().then((p) => setEnabled(p.aiEnabled)).catch(() => setEnabled(false)); }, []);
  const { said, turns } = thread;
  const [draft, setDraft] = useState('');
  const [at, setAt] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [doing, setDoing] = useState('');
  const bottom = useRef<HTMLDivElement>(null);

  useEffect(() => { bottom.current?.scrollIntoView({ block: 'end' }); }, [said, doing]);

  // "/" opens the list, and what follows narrows it. Names have spaces in
  // them, so the token runs to the end of the line rather than to the next
  // space — there is no way to type "Obras Tamboré" otherwise.
  const slash = /(?:^|\s)\/([^/]*)$/.exec(draft);
  // Preso a uma conversa, não há o que escolher — e oferecer a escolha
  // convidava a mudar de conversa num painel que é de uma só.
  const query = pinned || dismissed ? null : slash?.[1] ?? null;
  const matches = query === null ? []
    : chats.map((c) => titleOf(c))
        .filter((t) => fold(t).includes(fold(query)))
        .slice(0, 8);

  // The name leaves the text and becomes the thread's scope. It used to be
  // pasted into the question, which read badly and had to be repeated for
  // every follow-up.
  const pick = (title: string) => {
    const cut = slash ? slash.index + slash[0].indexOf('/') : draft.length;
    setDraft(draft.slice(0, cut));
    onThread({ ...thread, focus: title });
    setAt(0);
  };

  const send = async () => {
    const question = draft.trim();
    if (!question || busy) return;
    setDraft(''); setDismissed(false); setAt(0);
    const asked: Said[] = [...said, { mine: true, text: question }];
    onThread({ ...thread, said: asked, turns });
    setBusy(true); setDoing('Thinking…');
    const catalogue: Catalogue[] = chats.map((c) => ({ chat: c, title: titleOf(c) }));
    try {
      const r = await ask(question, turns, catalogue, accountKey, { round: setDoing },
        thread.focus ? [thread.focus] : []);
      onThread({ ...thread, said: [...asked, { mine: false, text: r.answer }], turns: r.turns });
    } catch (e) {
      onThread({ ...thread, said: [...asked, { mine: false, text: (e as Error).message }], turns });
    } finally { setBusy(false); setDoing(''); }
  };

  if (enabled === null) {
    return (
      <div className="askpanel">
      <header className="chat-bar">
        <button className="back" onClick={onBack}>
          <span aria-hidden="true">‹</span> Chats
        </button>
        <span className="chat-photo ask" aria-hidden="true">
          <svg viewBox="0 0 24 24" focusable="false">
            <path d="M12 3.2l1.7 4.1 4.1 1.7-4.1 1.7L12 14.8l-1.7-4.1L6.2 9l4.1-1.7z" />
            <path d="M18.2 15.4l.8 1.9 1.9.8-1.9.8-.8 1.9-.8-1.9-1.9-.8 1.9-.8z" />
          </svg>
        </span>
        <span className="chat-name">
          <strong>Ask</strong>
          <span className="roster">{pinned
            ? `Only about ${pinned}`
            : `Only about your ${chats.length} conversation${chats.length === 1 ? '' : 's'}`}</span>
        </span>
      </header>
      </div>
    );
  }

  if (!enabled) {
    return (
      <div className="askpanel">
      <header className="chat-bar">
        <button className="back" onClick={onBack}>
          <span aria-hidden="true">‹</span> Chats
        </button>
        <span className="chat-photo ask" aria-hidden="true">
          <svg viewBox="0 0 24 24" focusable="false">
            <path d="M12 3.2l1.7 4.1 4.1 1.7-4.1 1.7L12 14.8l-1.7-4.1L6.2 9l4.1-1.7z" />
            <path d="M18.2 15.4l.8 1.9 1.9.8-1.9.8-.8 1.9-.8-1.9-1.9-.8 1.9-.8z" />
          </svg>
        </span>
        <span className="chat-name">
          <strong>Ask</strong>
          <span className="roster">{pinned
            ? `Only about ${pinned}`
            : `Only about your ${chats.length} conversation${chats.length === 1 ? '' : 's'}`}</span>
        </span>
      </header>
        <div className="askoff">
          <p>Answering a question means sending the messages of that
             conversation, and your question, to <strong>Google</strong> —
             the model is Gemini 2.5 Flash, through Google Vertex AI. So this
             is off until you turn it on, and nothing is sent before you do.</p>
          <p>The switch is in your account, under <strong>AI features</strong>,
             which explains exactly what is sent and keeps a log of every time
             anything was read.</p>
          <button onClick={onEnable}>Turn on AI features</button>
        </div>
      </div>
    );
  }

  return (
    <div className="askpanel">
      <header className="chat-bar">
        <button className="back" onClick={onBack}>
          <span aria-hidden="true">‹</span> Chats
        </button>
        <span className="chat-photo ask" aria-hidden="true">
          <svg viewBox="0 0 24 24" focusable="false">
            <path d="M12 3.2l1.7 4.1 4.1 1.7-4.1 1.7L12 14.8l-1.7-4.1L6.2 9l4.1-1.7z" />
            <path d="M18.2 15.4l.8 1.9 1.9.8-1.9.8-.8 1.9-.8-1.9-1.9-.8 1.9-.8z" />
          </svg>
        </span>
        <span className="chat-name">
          <strong>Ask</strong>
          <span className="roster">{pinned
            ? `Only about ${pinned}`
            : `Only about your ${chats.length} conversation${chats.length === 1 ? '' : 's'}`}</span>
        </span>
      </header>

      <div className="thread">
        {said.length === 0 && (
          <div className="opening">
            <p>Ask about anything in your archive. It knows the names of your
               {' '}{chats.length} conversation{chats.length === 1 ? '' : 's'} and reads
               the ones it needs — on this device, with your key.</p>
            <ul>
              <li onClick={() => setDraft('What did we decide about the kitchen?')}>
                What did we decide about the kitchen?</li>
              <li onClick={() => setDraft('What is still unresolved across my conversations?')}>
                What is still unresolved?</li>
              <li onClick={() => setDraft('Summarise the last month.')}>
                Summarise the last month.</li>
            </ul>
          </div>
        )}
        {said.map((m, i) => (
          <div key={i} className={`askbubble ${m.mine ? 'mine' : 'theirs'}`}>{m.text}</div>
        ))}
        {busy && <div className="askbubble theirs doing">{doing}</div>}
        <div ref={bottom} />
      </div>

      <div className="askbar">
        {matches.length > 0 && (
          <ul className="picker" role="listbox" aria-label="Conversations">
            {matches.map((t, i) => (
              <li key={t} role="option" aria-selected={i === at}
                  className={i === at ? 'on' : ''}
                  onMouseDown={(e) => { e.preventDefault(); pick(t); }}>{t}</li>
            ))}
          </ul>
        )}
        {thread.focus && !pinned && (
          <span className="scope" title={`Only ${thread.focus}`}>
            {thread.focus}
            <button aria-label="Ask about everything again"
                    onClick={() => onThread({ ...thread, focus: undefined })}>×</button>
          </span>
        )}
        <input value={draft} disabled={busy} aria-label="Ask about your archive"
               // The chip already names the conversation; repeating it here
               // only squeezed the field on a phone until the placeholder was
               // cut in half.
               placeholder={pinned ? `Ask about ${pinned}…`
                          : thread.focus ? 'Ask…'
                          : 'Ask about your archive — type / for a conversation'}
               onChange={(e) => { setDraft(e.target.value); setDismissed(false); setAt(0); }}
               onKeyDown={(e) => {
                 if (matches.length > 0) {
                   if (e.key === 'ArrowDown') { e.preventDefault(); setAt((n) => (n + 1) % matches.length); return; }
                   if (e.key === 'ArrowUp') { e.preventDefault(); setAt((n) => (n - 1 + matches.length) % matches.length); return; }
                   if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pick(matches[at]!); return; }
                   if (e.key === 'Escape') { e.preventDefault(); setDismissed(true); return; }
                 }
                 if (e.key === 'Enter') void send();
               }} />
        <button disabled={busy || !draft.trim()} onClick={() => void send()}
                aria-label="Send">→</button>
      </div>
    </div>
  );
}
